const MONTH_MS = 30 * 24 * 60 * 60 * 1000;

interface StoredPayload<T> {
  value: T;
  expiresAt: number;
}

/**
 * Writes a value to localStorage with a 1-month expiry. Safe to call when
 * storage is unavailable (private browsing, quota) -- failures are logged
 * and ignored.
 */
export const setLocal = <T>(key: string, value: T, ttlMs: number = MONTH_MS): void => {
  try {
    const payload: StoredPayload<T> = { value, expiresAt: Date.now() + ttlMs };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch (error) {
    console.warn("Could not write to local storage", key, error);
  }
};

/**
 * Reads a value written by setLocal. Expired or malformed entries are removed
 * and treated as absent.
 */
export const getLocal = <T>(key: string): T | null => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const payload = JSON.parse(raw) as StoredPayload<T>;
    if (
      !payload ||
      typeof payload.expiresAt !== "number" ||
      payload.expiresAt < Date.now()
    ) {
      window.localStorage.removeItem(key);
      return null;
    }
    return payload.value;
  } catch {
    return null;
  }
};
